import {
    Body,
    Box,
    BrowseStepper,
    Flex,
    Grid,
    Heading,
    Hero,
    HtmlContent,
    LightTable,
    ListDivider,
    MediaModal,
    MediaSlider,
    Meta,
    SpecificationListItem,
    Stack,
    Tag,
    TextButton,
    TheaterModal,
    Tile,
    useTheaterModal,
    Wrapper,
} from 'designsystem';
import { GetStaticPaths, GetStaticProps } from 'next';
import { NextSeo } from 'next-seo';
import { useRouter } from 'next/router';
import React, { FC, useEffect, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { fetchApiData, gtm, LoadingFilmPage, slugifyTitle, useApi, useFilmInfo, VideoButtons } from 'shared';
import striptags from 'striptags';

import { useTheme } from '@emotion/react';
import { dehydrate, QueryClient, useQuery } from '@tanstack/react-query';

import ProfessionalsBreadcrumbs from '../../../components/ProfessionalsBreadcrumbs';
import {
    ProjectSpaceFilmDocument,
    ProjectSpaceFilmQuery,
    ProjectSpaceFilmQueryVariables,
    ProjectSpaceFilterEnum,
    useProjectSpaceFilmQuery,
    useProjectSpaceFilmStepperQuery,
} from '../../../gql/api';
import { useProjectSpacePathQuery } from '../../../gql/cms';
import fetchProjectSpaceUri from '../../../utils/fetchProjectSpaceUri';
import loadIntlMessages from '../../../utils/loadIntlMessages';
import prefetchGlobals from '../../../utils/prefetchGlobals';
import siteHandleByLocale from '../../../utils/siteHandleByLocale';
import { NumberParam, useQueryParam, useQueryParams } from 'use-query-params';
import { ProjectSpaceFilterQueryParams } from '../../../components/pages/ProjectSpaceCollectionPage';
import useBrowseStepper, { getStepperQueryVars, StepperData } from '../../../hooks/useBrowseStepper';
import { DEFAULT_REVALIDATION_TIME } from '../../[...uri]';
import { useSession } from 'next-auth/react';

const ProjectSpaceDetail: FC = () => {
    const { query, asPath, locale, isFallback } = useRouter();
    const { status } = useSession();
    const collectionUri = useProjectSpacePathQuery({
        site: siteHandleByLocale[locale],
    }).data?.entry?.uri;

    const { tokens } = useTheme();
    const vars = {
        id: query.id as string,
    };
    const { data, isLoading } = useQuery(
        [...useProjectSpaceFilmQuery.getKey(vars), { loggedIn: status === 'authenticated' }],
        useApi<ProjectSpaceFilmQuery, ProjectSpaceFilmQueryVariables>(ProjectSpaceFilmDocument).bind(null, vars)
    );
    const film = data?.film;

    const {
        title,
        translatedTitle,
        meta,
        description,
        lengthInMinutes,
        ogImage,
        edition,
        requestTypes,
        mostRecentEdition,
        countriesOfProduction,
        countriesOfCoproduction,
        productionStatus,
        sections,
        inArchive,
        films,
        trailers,
        mediaImages,
        mediaVideos,
        mediaModalProps,
        subjects,
        heroImage,
        credits,
    } = useFilmInfo({ film, filmOrProjectType: 'projectSpace' });

    useEffect(() => {
        if (film) {
            gtm.pageView(asPath, {
                page_pillar: 'professionals',
                page_navigation: collectionUri,
                page_language: locale,
                film_id: film.id,
                film_name: film.fullPreferredTitle,
                film_edition: film.edition.year.toString(),
                film_section: film.sections?.join(', ') ?? '',
                film_subject:
                    film.subject?.map(s => s.translation).join(', ') ??
                    film.tags?.map(tag => tag.translation).join(', ') ??
                    '',
            });
        }
    }, [film, asPath, collectionUri, subjects, locale]);

    const projectInformation = useMemo(
        () =>
            [
                requestTypes && {
                    key: 'category',
                    description: <FormattedMessage defaultMessage="Category" />,
                    value: requestTypes,
                },
                countriesOfProduction && {
                    key: 'countries-of-production',
                    description: <FormattedMessage defaultMessage="Countries of Production" />,
                    value: countriesOfProduction,
                },
                countriesOfCoproduction && {
                    key: 'countries-of-co‑production',
                    description: <FormattedMessage defaultMessage="Countries of co‑production" />,
                    value: countriesOfCoproduction,
                },
                productionStatus && {
                    key: 'spoken-languages',
                    description: <FormattedMessage defaultMessage="Current Production Status" />,
                    value: productionStatus,
                },
                lengthInMinutes && {
                    key: 'available-versions',
                    description: <FormattedMessage defaultMessage="Estimated Length" />,
                    value: lengthInMinutes,
                },
            ].filter(Boolean),
        [requestTypes, countriesOfProduction, countriesOfCoproduction, productionStatus, lengthInMinutes]
    );

    // browse stepper
    const [filterParams] = useQueryParams(ProjectSpaceFilterQueryParams);
    const [offset] = useQueryParam('offset', NumberParam);
    const { data: stepperData, isLoading: isStepperLoading } = useProjectSpaceFilmStepperQuery(
        getStepperQueryVars<ProjectSpaceFilterEnum>(filterParams, offset, query.query as string),
        { enabled: offset >= 0 }
    );

    const { nextFilm, current, total, onNext, onPrev } = useBrowseStepper(
        stepperData?.searchProjectSpace as StepperData,
        ProjectSpaceFilterQueryParams
    );

    const browseStepper = (
        <BrowseStepper
            title={<FormattedMessage defaultMessage="Explore the collection" />}
            body={
                <FormattedMessage
                    defaultMessage="{current, number}/{total, number} {total, plural, one {result} other {results}}"
                    values={{ current, total }}
                />
            }
            isLoading={isStepperLoading}
            current={offset + 1}
            total={total}
            prevDisabled={offset === 0}
            nextDisabled={!nextFilm}
            onPrev={onPrev}
            onNext={onNext}
        />
    );

    const { videoSrc, handleVideoClick, ...disclosureProps } = useTheaterModal();

    if (isFallback || isLoading) {
        return <LoadingFilmPage />;
    }

    return (
        film && (
            <>
                <NextSeo
                    title={`${title} (${edition.year})`}
                    description={striptags(description)}
                    openGraph={{
                        images: ogImage,
                    }}
                    // the most recent film is the 'canonical' film to prevent a film from getting duplicated in search results.
                    canonical={
                        mostRecentEdition &&
                        `${process.env.NEXT_PUBLIC_BASE_URL ?? ''}/project-space/${mostRecentEdition.id}/${slugifyTitle(
                            title
                        )}`
                    }
                    titleTemplate="%s | IDFA Project Space"
                />
                <Hero
                    image={heroImage}
                    topChildren={
                        <ProfessionalsBreadcrumbs
                            parentUri={collectionUri}
                            currentPage={{
                                title,
                                uri: asPath,
                            }}
                            onImage
                        />
                    }
                    rightChildren={
                        inArchive && (
                            <Tile colorScheme="institute" gridRow="2">
                                <Stack spacing={6}>
                                    <Stack spacing={4}>
                                        <Heading variant={2} as="h2">
                                            <FormattedMessage defaultMessage="This project is now part of the IDFA Archive" />
                                        </Heading>
                                        <Body>
                                            <FormattedMessage defaultMessage="The IDFA Archive on the Institute site contains the resulting film." />
                                        </Body>
                                    </Stack>
                                    <TextButton
                                        size="s"
                                        href={`${process.env.NEXT_PUBLIC_PILLAR_URL_INSTITUTE}/${locale}/film/${film.id}/${query.slug}`}
                                    >
                                        <FormattedMessage defaultMessage="View details in the Archive" />
                                    </TextButton>
                                </Stack>
                            </Tile>
                        )
                    }
                >
                    <Flex flexDirection="column">
                        <Stack spacing={[4, null, 6]}>
                            <Tag size="l">
                                <Body fontSize={[4, null, 5]}>
                                    <FormattedMessage defaultMessage="IDFA Project Space" />
                                </Body>
                            </Tag>
                            <Stack spacing={4}>
                                <Stack spacing="2">
                                    <Heading as="h1" variant={1} clamp={3}>
                                        {title}
                                    </Heading>
                                    {translatedTitle && <Heading variant={5}>{translatedTitle}</Heading>}
                                </Stack>
                                <Meta {...meta} size="m" />
                            </Stack>
                            <VideoButtons
                                films={films}
                                trailers={trailers}
                                handleVideoClick={handleVideoClick}
                                filmExternal
                            />
                        </Stack>
                    </Flex>
                </Hero>
                <TheaterModal
                    title={film.fullPreferredTitle}
                    meta={{ ...meta }}
                    videoSrc={videoSrc}
                    {...disclosureProps}
                />
                <Flex flexDir="column" position="relative" borderBottom="1px solid" borderColor="neutral.30">
                    <Box
                        as="section"
                        borderBottomWidth={tokens.BorderWidthM}
                        borderBottomStyle="solid"
                        borderBottomColor={tokens.BorderColorNeutralDefault}
                    >
                        <Wrapper>
                            <ListDivider opacity={0.2} />
                            <Stack
                                direction={['column', 'row']}
                                spacing={[6, 8]}
                                divider={<ListDivider />}
                                py={[6, null, 8]}
                            >
                                <SpecificationListItem
                                    title={<FormattedMessage defaultMessage="Selected in" />}
                                    value={edition.year}
                                />
                                {requestTypes && (
                                    <SpecificationListItem
                                        title={<FormattedMessage defaultMessage="Program" />}
                                        value={requestTypes}
                                    />
                                )}
                                {sections && (
                                    <SpecificationListItem
                                        title={<FormattedMessage defaultMessage="IDFA Program Section" />}
                                        value={sections}
                                    />
                                )}
                            </Stack>
                        </Wrapper>
                    </Box>
                    <Box
                        display={['none', null, null, null, 'block']}
                        position="absolute"
                        top={0}
                        right={0}
                        minH="144px"
                        py={8}
                        px="84px"
                        bgColor="neutral.10"
                    >
                        {browseStepper}
                    </Box>
                </Flex>
                <Stack py={[9, null, 11]} spacing={[9, null, 11]}>
                    <Wrapper>
                        <Grid>
                            <Stack
                                gridColumn={['1 / -1', null, '1 / span 9', '1 / span 8', '1 / span 7']}
                                spacing={[9, null, 11]}
                            >
                                {description && (
                                    <Stack as="section" spacing={6}>
                                        <Heading variant={3} as="h2">
                                            <FormattedMessage defaultMessage="Logline" />
                                        </Heading>
                                        <Body>
                                            <Stack spacing={4}>
                                                <HtmlContent html={description} />
                                            </Stack>
                                        </Body>
                                    </Stack>
                                )}
                                <Box
                                    display={['flex', null, null, null, 'none']}
                                    bg="neutral.10"
                                    h="100%"
                                    w="100%"
                                    justifyContent="center"
                                    py={9}
                                >
                                    {browseStepper}
                                </Box>
                                {projectInformation.length > 0 && (
                                    <Stack as="section" spacing={6}>
                                        <Heading variant={3} as="h2">
                                            <FormattedMessage defaultMessage="Project Information" />
                                        </Heading>
                                        <LightTable lines={projectInformation} />
                                    </Stack>
                                )}
                                {credits.length > 0 && (
                                    <Stack as="section" spacing={6}>
                                        <Heading variant={3} as="h2">
                                            <FormattedMessage defaultMessage="Credits" />
                                        </Heading>
                                        <LightTable lines={credits} />
                                    </Stack>
                                )}
                            </Stack>
                        </Grid>
                    </Wrapper>
                    {(mediaVideos.length > 0 || mediaImages.length > 0) && (
                        <Stack as="section" spacing={6}>
                            <Wrapper>
                                <Heading variant={3} as="h2">
                                    <FormattedMessage defaultMessage="Media" />
                                </Heading>
                            </Wrapper>
                            <MediaSlider media={[...mediaVideos, ...mediaImages].filter(Boolean)} />
                            <MediaModal
                                {...mediaModalProps}
                                closeLabel={<FormattedMessage defaultMessage="Close" description="Close modal" />}
                            />
                        </Stack>
                    )}
                </Stack>
            </>
        )
    );
};

export const getStaticPaths: GetStaticPaths = async () => ({
    // we don't provide paths because we won't fetch any films during build time.
    paths: [],

    fallback: true,
});

export const getStaticProps: GetStaticProps = async ({ params, locale }) => {
    const queryClient = new QueryClient();

    const variables = {
        id: params?.id as string,
    };
    const data = await queryClient.fetchQuery(
        [...useProjectSpaceFilmQuery.getKey(variables), { loggedIn: false }],
        () =>
            fetchApiData<ProjectSpaceFilmQuery, ProjectSpaceFilmQueryVariables>({
                locale,
                query: useProjectSpaceFilmQuery.document,
                variables,
            })
    );
    if (!data.film) {
        return {
            notFound: true,
            revalidate: DEFAULT_REVALIDATION_TIME,
        };
    }

    const collectionUri = await fetchProjectSpaceUri(queryClient, locale);
    await prefetchGlobals(queryClient, locale, { pathType: 'PARENT_URI', uri: collectionUri });

    return {
        props: {
            intlMessages: await loadIntlMessages(locale),
            dehydratedState: dehydrate(queryClient),
        },
        revalidate: DEFAULT_REVALIDATION_TIME,
    };
};

export default ProjectSpaceDetail;
